/**
 * EmailEditor - WYSIWYG email content editor with HTML toggle.
 *
 * Uses TipTap for rich text editing with a Clara Vale styled toolbar.
 * Supports bold, italic, headings, links, lists, and images.
 * Toggle to raw HTML view for advanced users.
 */

import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import { useState, useEffect, useCallback } from 'react'
import { Label } from '@/shared/components/ui/label'
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  Heading2,
  Code2,
  Undo,
  Redo,
  Minus,
} from 'lucide-react'

interface EmailEditorProps {
  value: string
  onChange: (html: string) => void
  label?: string
  minHeight?: number
  maxHeight?: number
  htmlRows?: number
  /** Where the editor opens. Senders who are not template authors want visual. */
  defaultMode?: 'html' | 'visual'
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`h-7 w-7 flex items-center justify-center rounded-[3px] transition-all duration-100 cursor-pointer disabled:opacity-30 ${
        active
          ? 'bg-[var(--green-bg-muted)] text-[var(--green)]'
          : 'text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]'
      }`}
    >
      {children}
    </button>
  )
}

function Toolbar({ editor }: { editor: Editor }) {
  const addLink = useCallback(() => {
    const url = window.prompt('URL:')
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }
  }, [editor])

  const addImage = useCallback(() => {
    const url = window.prompt('Image URL:')
    if (url) {
      editor.chain().focus().setImage({ src: url }).run()
    }
  }, [editor])

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b-[0.5px] border-b-[var(--rule)] px-2 py-1.5">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolbarButton>

      <div className="mx-1 h-4 w-px bg-[var(--rule)]" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })}
        title="Heading"
      >
        <Heading2 className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="Bullet list"
      >
        <List className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="Numbered list"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarButton>

      <div className="mx-1 h-4 w-px bg-[var(--rule)]" />

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        active={editor.isActive({ textAlign: 'left' })}
        title="Align left"
      >
        <AlignLeft className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        active={editor.isActive({ textAlign: 'center' })}
        title="Align center"
      >
        <AlignCenter className="h-3.5 w-3.5" />
      </ToolbarButton>

      <div className="mx-1 h-4 w-px bg-[var(--rule)]" />

      <ToolbarButton onClick={addLink} active={editor.isActive('link')} title="Add link">
        <LinkIcon className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={addImage} title="Add image">
        <ImageIcon className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal rule"
      >
        <Minus className="h-3.5 w-3.5" />
      </ToolbarButton>

      <div className="flex-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo className="h-3.5 w-3.5" />
      </ToolbarButton>
    </div>
  )
}

export function EmailEditor({
  value,
  onChange,
  label,
  minHeight = 240,
  maxHeight = 480,
  htmlRows = 16,
  defaultMode = 'html',
}: EmailEditorProps) {
  const [isHtmlMode, setIsHtmlMode] = useState(defaultMode === 'html')

  // TipTap (Visual mode) has no table support, so a fixed table layout gets
  // flattened into paragraphs (and buttons repainted) the moment it round-trips
  // through the rich-text editor. Lock such templates to HTML source mode so
  // editing on the platform can't silently destroy the layout.
  const structuredLayout = /<table[\s>]/i.test(value)
  const htmlMode = isHtmlMode || structuredLayout

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Disable StarterKit's built-in Link so the manual Link.configure
        // below is the only one - duplicates crash the editor on mount.
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        // No forced inline style: hardcoding a button style here repainted
        // every link (incl. brand-coloured CTA buttons) a fixed green on any
        // Visual-mode round-trip. Button styling belongs to the template HTML.
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
        // Allow Jinja/Django template hrefs like `{{ session.url }}` to
        // survive a round-trip through the Visual Editor. The default URL
        // validator would reject these as malformed and TipTap would then
        // strip the entire <a>, leaving just the link text behind.
        isAllowedUri: (uri) => {
          if (!uri) return false
          if (uri.startsWith('{{') || uri.includes('{{')) return true
          return /^(https?:|mailto:|tel:|#|\/)/i.test(uri)
        },
      }),
      Image.configure({
        inline: false,
        HTMLAttributes: { style: 'max-width: 100%; border-radius: 4px;' },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: value,
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML())
    },
  })

  // Sync external `value` changes into the editor - visual mode only.
  // In HTML mode the textarea is controlled directly by `value`, so any
  // detour through TipTap (which normalises inline styles + drops
  // unknown attributes) would silently rewrite the admin's source. The
  // editor remains mounted but isolated until the admin toggles back.
  useEffect(() => {
    if (htmlMode) return
    if (editor && !editor.isFocused && value !== editor.getHTML()) {
      editor.commands.setContent(value, false)
    }
  }, [value, editor, htmlMode])

  function handleToggleMode() {
    // A fixed table layout can only be edited safely as HTML source.
    if (structuredLayout) return
    // HTML → Visual: hand the textarea's current value to the editor.
    // Visual → HTML: nothing to do - `value` is already current via
    // the editor's onUpdate callback.
    if (isHtmlMode) {
      editor?.commands.setContent(value, false)
    }
    setIsHtmlMode(!isHtmlMode)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        {label && <Label className="clara-section-label">{label}</Label>}
        <button
          type="button"
          onClick={handleToggleMode}
          disabled={structuredLayout}
          title={
            structuredLayout
              ? 'This template uses a fixed table layout - edit it in HTML source so the layout is not flattened'
              : undefined
          }
          className={`flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] px-2 py-1 rounded-[3px] transition-all duration-150 ${
            structuredLayout
              ? 'text-[var(--ink-3)] opacity-40 cursor-not-allowed'
              : htmlMode
                ? 'text-[var(--green)] bg-[var(--green-bg-muted)] cursor-pointer'
                : 'text-[var(--ink-3)] hover:bg-[var(--bg-subtle)] cursor-pointer'
          }`}
        >
          <Code2 className="h-3 w-3" />
          {structuredLayout
            ? 'HTML source (fixed layout)'
            : htmlMode
              ? 'Visual editor'
              : 'HTML source'}
        </button>
      </div>
      {htmlMode ? (
        <div>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={htmlRows}
            className="w-full resize-y rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2.5 font-mono text-[12px] text-[var(--ink)]"
          />
          {structuredLayout && (
            <p className="mt-1.5 text-[10px] text-[var(--ink-3)]">
              This template uses a fixed table layout, so it is edited as HTML
              to keep the design intact.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-[4px] border-[0.5px] border-[var(--rule)]">
          {editor && <Toolbar editor={editor} />}
          <EditorContent
            editor={editor}
            className="email-editor-content"
          />
          <style>{`
            .email-editor-content .tiptap {
              min-height: ${minHeight}px;
              max-height: ${maxHeight}px;
              overflow-y: auto;
              padding: 16px;
              font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #1a1a18;
              outline: none;
            }
            .email-editor-content .tiptap p {
              margin: 0 0 8px 0;
            }
            .email-editor-content .tiptap h1,
            .email-editor-content .tiptap h2,
            .email-editor-content .tiptap h3 {
              font-weight: 600;
              margin: 16px 0 8px 0;
              color: #1a1a18;
            }
            .email-editor-content .tiptap h2 {
              font-size: 18px;
            }
            .email-editor-content .tiptap a {
              display: inline-block;
              background-color: #1c5a48;
              color: #ffffff;
              padding: 12px 24px;
              border-radius: 6px;
              text-decoration: none;
              font-weight: 600;
            }
            .email-editor-content .tiptap a:hover {
              background-color: #164838;
            }
            .email-editor-content .tiptap ul,
            .email-editor-content .tiptap ol {
              padding-left: 24px;
              margin: 8px 0;
            }
            .email-editor-content .tiptap img {
              max-width: 100%;
              border-radius: 4px;
              margin: 8px 0;
            }
            .email-editor-content .tiptap hr {
              border: none;
              border-top: 1px solid var(--rule);
              margin: 16px 0;
            }
            .email-editor-content .tiptap p.is-editor-empty:first-child::before {
              content: 'Start typing your email content...';
              color: #9a9a94;
              float: left;
              height: 0;
              pointer-events: none;
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
